import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Btn } from "@/components/ui/Btn/Btn";
import { Icon } from "@/components/ui/Icon/Icon";
import { Input } from "@/components/ui/Input/Input";
import { TagInput } from "@/components/ui/TagInput/TagInput";
import { Textarea } from "@/components/ui/Textarea/Textarea";
import { AppShell } from "@/components/app/AppShell";
import { Field } from "@/components/auth/FormShell";
import { useAuth } from "@/lib/AuthContext";
import { useBrand, useUpdateBrand } from "@/lib/queries";
import type { BrandBrain } from "@notamen/core/brand";
import styles from "./BrandBrainEditor.module.css";

type Platform = "instagram" | "facebook" | "tiktok";
const PLATFORM_DEFAULTS: Record<Platform, { maxCaptionChars: number; maxHashtags: number }> = {
  instagram: { maxCaptionChars: 2200, maxHashtags: 8 },
  facebook: { maxCaptionChars: 5000, maxHashtags: 3 },
  tiktok: { maxCaptionChars: 2200, maxHashtags: 5 },
};

export function BrandBrainEditor() {
  const { ws, brand } = useParams<{ ws: string; brand: string }>();
  const auth = useAuth();
  const navigate = useNavigate();
  const brandQuery = useBrand(ws, brand);
  const updateBrand = useUpdateBrand(ws, brand);

  const [draft, setDraft] = useState<BrandBrain | null>(null);
  // Holds the last-known full rules per platform so that toggling a
  // platform off and back on restores its sub-fields (denyHashtags,
  // aspect ratios, etc.) instead of a 2-field default stub.
  const platformStash = useRef<BrandBrain["platformRules"]>({});

  // Hydrate the editable draft when the brand loads, AND re-hydrate when
  // the loaded brand differs from the current draft (the editor instance
  // is reused across /b/:brand/edit param changes, so without this an
  // edit→edit navigation could save brand A's brain onto brand B).
  useEffect(() => {
    const loaded = brandQuery.data?.brand;
    if (!loaded) return;
    if (draft && draft.id === loaded.id) return;
    // Strip the row metadata (workspaceId/createdAt/updatedAt) — the
    // editor works on the BrandBrain shape the PUT endpoint expects.
    const { workspaceId, createdAt, updatedAt, ...brain } = loaded;
    void workspaceId;
    void createdAt;
    void updatedAt;
    platformStash.current = { ...(brain as BrandBrain).platformRules };
    setDraft(brain as BrandBrain);
  }, [brandQuery.data, draft]);

  if (auth.status === "anonymous") {
    return <Navigate to={`/signin?next=/w/${ws}/b/${brand}/edit`} replace />;
  }

  function patch(partial: Partial<BrandBrain>) {
    setDraft((d) => (d ? { ...d, ...partial } : d));
  }

  function setSourceKind(kind: string) {
    if (kind === "website") {
      const existing =
        draft?.productSource?.kind === "website" ? draft.productSource.siteUrl : "";
      patch({ productSource: { kind: "website", siteUrl: existing || draft?.website || "" } });
    } else if (kind === "manual") {
      patch({ productSource: { kind: "manual" } });
    } else {
      patch({ productSource: { kind: "none" } });
    }
  }

  function togglePlatform(p: Platform, on: boolean) {
    setDraft((d) => {
      if (!d) return d;
      if (!on) {
        // Stash current rules (incl. sub-fields the form doesn't render)
        // before removing, so re-checking restores them.
        const current = d.platformRules[p];
        if (current) platformStash.current = { ...platformStash.current, [p]: current };
        const next = { ...d.platformRules };
        delete next[p];
        return { ...d, platformRules: next };
      }
      // Computed-key spread (not direct assignment) so TS checks the
      // value loosely against the per-platform union.
      const restored = platformStash.current[p] ?? PLATFORM_DEFAULTS[p];
      return { ...d, platformRules: { ...d.platformRules, [p]: restored } };
    });
  }

  // A website product source needs a valid URL or the brand PUT (zod
  // url()) rejects the whole save.
  const sourceOk =
    !draft ||
    draft.productSource?.kind !== "website" ||
    isHttpUrl(draft.productSource.siteUrl);

  // Server zod accepts empty strings for these, but an empty core field
  // produces a degraded brain — block save until they're filled.
  const canSave =
    !!draft &&
    !!draft.name.trim() &&
    !!draft.elevator.trim() &&
    !!draft.audience.primary.trim() &&
    !!draft.visualStyle.look.trim() &&
    sourceOk;

  async function onSave() {
    if (!draft || !canSave) return;
    await updateBrand.mutateAsync(draft);
    navigate(`/w/${ws}/b/${brand}`);
  }

  return (
    <AppShell currentWorkspaceId={ws}>
      <div className={styles.page}>
        <header className={styles.head}>
          <div>
            <Btn href={`/w/${ws}/b/${brand}`} variant="ghost" size="sm" iconLeft={<Icon name="arrow-left" size={13} />}>
              Back to brand
            </Btn>
            <h1 className={styles.title}>Edit brand brain</h1>
            <p className={styles.sub}>
              Everything here feeds the AI. The more you fill in, the more drafts sound
              like you. Empty fields are simply skipped in the prompt.
            </p>
          </div>
        </header>

        {brandQuery.isLoading && <p className={styles.muted}>Loading brand…</p>}
        {brandQuery.error && (
          <p className={styles.error}>Couldn’t load this brand: {brandQuery.error.message}</p>
        )}

        {draft && (
          <div className={styles.sections}>
            {/* Basics */}
            <Section title="Basics">
              <Field label="Name" htmlFor="b-name">
                <Input id="b-name" value={draft.name} onChange={(e) => patch({ name: e.target.value })} />
              </Field>
              <Field label="Tagline" htmlFor="b-tagline">
                <Input
                  id="b-tagline"
                  value={draft.tagline ?? ""}
                  onChange={(e) => patch({ tagline: e.target.value || undefined })}
                  placeholder="Short and punchy"
                />
              </Field>
              <Field label="Website" htmlFor="b-website">
                <Input
                  id="b-website"
                  value={draft.website ?? ""}
                  onChange={(e) => patch({ website: e.target.value || undefined })}
                  placeholder="https://…"
                />
              </Field>
              <Field label="Elevator pitch" htmlFor="b-elevator" hint="One or two sentences on what the brand is.">
                <Textarea
                  id="b-elevator"
                  value={draft.elevator}
                  onChange={(e) => patch({ elevator: e.target.value })}
                />
              </Field>
            </Section>

            {/* Audience */}
            <Section title="Audience">
              <Field label="Primary audience" htmlFor="b-aud-primary">
                <Input
                  id="b-aud-primary"
                  value={draft.audience.primary}
                  onChange={(e) => patch({ audience: { ...draft.audience, primary: e.target.value } })}
                />
              </Field>
              <Field label="Secondary audience" htmlFor="b-aud-secondary">
                <Input
                  id="b-aud-secondary"
                  value={draft.audience.secondary ?? ""}
                  onChange={(e) =>
                    patch({ audience: { ...draft.audience, secondary: e.target.value || undefined } })
                  }
                />
              </Field>
              <Field label="Psychographics" hint="Values, interests, identity markers. Enter to add.">
                <TagInput
                  value={draft.audience.psychographics}
                  onChange={(v) => patch({ audience: { ...draft.audience, psychographics: v } })}
                  placeholder="eco-conscious, time-poor, …"
                />
              </Field>
            </Section>

            {/* Voice & tone */}
            <Section title="Voice & tone">
              <Field label="Voice is" hint="Adjectives that describe how the brand sounds.">
                <TagInput
                  value={draft.tone.voiceAdjectives}
                  onChange={(v) => patch({ tone: { ...draft.tone, voiceAdjectives: v } })}
                  placeholder="warm, irreverent, …"
                />
              </Field>
              <Field label="Voice is NOT" hint="Adjectives to steer away from.">
                <TagInput
                  value={draft.tone.antiAdjectives}
                  onChange={(v) => patch({ tone: { ...draft.tone, antiAdjectives: v } })}
                  placeholder="corporate, salesy, …"
                />
              </Field>
              <Field label="Words to use">
                <TagInput
                  value={draft.tone.lexicon.use}
                  onChange={(v) => patch({ tone: { ...draft.tone, lexicon: { ...draft.tone.lexicon, use: v } } })}
                />
              </Field>
              <Field label="Words to avoid">
                <TagInput
                  value={draft.tone.lexicon.avoid}
                  onChange={(v) => patch({ tone: { ...draft.tone, lexicon: { ...draft.tone.lexicon, avoid: v } } })}
                />
              </Field>
              <Field label="Writing rules" hint="e.g. 'never use exclamation marks', 'lowercase captions'.">
                <TagInput
                  value={draft.tone.writingRules}
                  onChange={(v) => patch({ tone: { ...draft.tone, writingRules: v } })}
                />
              </Field>
            </Section>

            {/* Products */}
            <Section title="Products & categories">
              <Field label="Product categories" hint="Free-text descriptors used when there's no live catalog.">
                <TagInput
                  value={draft.productCategories}
                  onChange={(v) => patch({ productCategories: v })}
                  placeholder="one product line per tag, …"
                />
              </Field>
            </Section>

            {/* Product source */}
            <Section title="Product source">
              {draft.productSource &&
              (draft.productSource.kind === "shopify" ||
                draft.productSource.kind === "woocommerce") ? (
                <p className={styles.muted}>
                  This brand syncs products from{" "}
                  {draft.productSource.kind === "shopify" ? "Shopify" : "WooCommerce"}, configured
                  via store credentials. Manage it from your store settings.
                </p>
              ) : (
                <>
                  <Field
                    label="Where products come from"
                    hint="“Website crawl” discovers products from your public site — no API keys needed."
                  >
                    <select
                      className={styles.select}
                      value={draft.productSource?.kind ?? "none"}
                      onChange={(e) => setSourceKind(e.target.value)}
                    >
                      <option value="none">No product catalog</option>
                      <option value="website">Website crawl</option>
                      <option value="manual">Manual entry</option>
                    </select>
                  </Field>
                  {draft.productSource?.kind === "website" && (
                    <Field
                      label="Site URL"
                      htmlFor="b-siteurl"
                      hint="The storefront we’ll crawl (sitemap + product pages)."
                    >
                      <Input
                        id="b-siteurl"
                        value={draft.productSource.siteUrl}
                        onChange={(e) =>
                          patch({ productSource: { kind: "website", siteUrl: e.target.value } })
                        }
                        placeholder="https://yourstore.com"
                      />
                    </Field>
                  )}
                </>
              )}
            </Section>

            {/* Things to avoid */}
            <Section title="Things to avoid">
              <Field label="Banned terms">
                <TagInput
                  value={draft.thingsToAvoid.bannedTerms}
                  onChange={(v) => patch({ thingsToAvoid: { ...draft.thingsToAvoid, bannedTerms: v } })}
                />
              </Field>
              <Field label="Claims we can't make">
                <TagInput
                  value={draft.thingsToAvoid.noClaims}
                  onChange={(v) => patch({ thingsToAvoid: { ...draft.thingsToAvoid, noClaims: v } })}
                />
              </Field>
              <Field label="Visuals to avoid">
                <TagInput
                  value={draft.thingsToAvoid.noVisuals}
                  onChange={(v) => patch({ thingsToAvoid: { ...draft.thingsToAvoid, noVisuals: v } })}
                />
              </Field>
              <Field label="Behaviour to avoid">
                <TagInput
                  value={draft.thingsToAvoid.noBehavior}
                  onChange={(v) => patch({ thingsToAvoid: { ...draft.thingsToAvoid, noBehavior: v } })}
                />
              </Field>
            </Section>

            {/* Visual style */}
            <Section title="Visual style">
              <Field label="Look & feel" htmlFor="b-look">
                <Textarea
                  id="b-look"
                  value={draft.visualStyle.look}
                  onChange={(e) => patch({ visualStyle: { ...draft.visualStyle, look: e.target.value } })}
                  placeholder="Sun-faded film grain, candid moments over staged shots…"
                />
              </Field>
              <Field label="Primary palette" hint="Hex or names.">
                <TagInput
                  value={draft.visualStyle.palette.primary}
                  onChange={(v) =>
                    patch({ visualStyle: { ...draft.visualStyle, palette: { ...draft.visualStyle.palette, primary: v } } })
                  }
                  placeholder="#0f0a05, ember orange, …"
                />
              </Field>
              <Field label="Accent palette">
                <TagInput
                  value={draft.visualStyle.palette.accent}
                  onChange={(v) =>
                    patch({ visualStyle: { ...draft.visualStyle, palette: { ...draft.visualStyle.palette, accent: v } } })
                  }
                />
              </Field>
              <Field label="Composition rules">
                <TagInput
                  value={draft.visualStyle.compositionRules}
                  onChange={(v) => patch({ visualStyle: { ...draft.visualStyle, compositionRules: v } })}
                />
              </Field>
            </Section>

            {/* Platform rules */}
            <Section title="Platform rules">
              {(["instagram", "facebook", "tiktok"] as Platform[]).map((p) => {
                const rules = draft.platformRules[p];
                return (
                  <div key={p} className={styles.platform}>
                    <label className={styles.platformToggle}>
                      <input
                        type="checkbox"
                        checked={!!rules}
                        onChange={(e) => togglePlatform(p, e.target.checked)}
                      />
                      <span className={styles.platformName}>{p}</span>
                    </label>
                    {rules && (
                      <div className={styles.platformFields}>
                        <Field label="Max caption chars">
                          <Input
                            type="number"
                            min={1}
                            value={String(rules.maxCaptionChars)}
                            onChange={(e) => {
                              const n = parseInt(e.target.value, 10);
                              patch({
                                platformRules: {
                                  ...draft.platformRules,
                                  [p]: {
                                    ...rules,
                                    maxCaptionChars: Number.isFinite(n) ? n : rules.maxCaptionChars,
                                  },
                                },
                              });
                            }}
                          />
                        </Field>
                        <Field label="Max hashtags">
                          <Input
                            type="number"
                            min={0}
                            value={String(rules.maxHashtags)}
                            onChange={(e) => {
                              const n = parseInt(e.target.value, 10);
                              patch({
                                platformRules: {
                                  ...draft.platformRules,
                                  [p]: {
                                    ...rules,
                                    maxHashtags: Number.isFinite(n) ? n : rules.maxHashtags,
                                  },
                                },
                              });
                            }}
                          />
                        </Field>
                      </div>
                    )}
                  </div>
                );
              })}
            </Section>

            {/* Recommended hashtags */}
            <Section title="Recommended hashtags">
              <Field
                label="Quick-add hashtags"
                hint="Curated tags a teammate can one-click add while editing a draft in the approval queue. The # is optional."
              >
                <TagInput
                  value={draft.recommendedHashtags ?? []}
                  onChange={(v) => patch({ recommendedHashtags: v })}
                  placeholder="festivalparent, summervibes, …"
                />
              </Field>
            </Section>

            {/* Compliance */}
            <Section title="Compliance notes">
              <Field label="Notes" hint="Regulatory or legal constraints the AI must respect.">
                <TagInput
                  value={draft.complianceNotes}
                  onChange={(v) => patch({ complianceNotes: v })}
                />
              </Field>
            </Section>
          </div>
        )}

        {draft && (
          <footer className={styles.actions}>
            {updateBrand.error && <span className={styles.error}>{updateBrand.error.message}</span>}
            {!canSave && !updateBrand.error && (
              <span className={styles.muted}>
                Name, elevator, primary audience, and look &amp; feel are required.
              </span>
            )}
            <Btn variant="secondary" href={`/w/${ws}/b/${brand}`}>
              Cancel
            </Btn>
            <Btn
              variant="primary"
              onClick={onSave}
              loading={updateBrand.isPending}
              disabled={!canSave}
              iconRight={<Icon name="check" size={14} />}
            >
              Save brand brain
            </Btn>
          </footer>
        )}
      </div>
    </AppShell>
  );
}

function isHttpUrl(v: string): boolean {
  try {
    const u = new URL(v);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>{title}</h2>
      <div className={styles.sectionBody}>{children}</div>
    </section>
  );
}
